/* eslint-disable jsdoc/require-jsdoc, no-nested-ternary */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import moment from 'moment';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';
import Loader from '../components/loader';

const ADMIN_EMAIL = 'suresh@potsdamsensors.com';

type LastPayment = { date: number; amount: number | null; currency: string | null; planName: string | null };

type ClientRow = {
  clientId: number;
  clientName: string;
  status: string | null;
  deviceCount: number;
  activeDevices: number;
  lastSeen: number | null;
  lastCalibration: string | null;
  lastPayment: LastPayment | null;
};

type DeviceRow = {
  serialNumber: number;
  deviceName: string | null;
  clientId: number;
  clientName: string;
  deviceType: string | null;
  status: string | null;
  lastSeen: number | null;
  active: boolean;
  lastCalibration: string | null;
};

type AdminOverview = {
  generatedAt: number;
  activeWindowMinutes: number;
  lastSeenLookbackDays: number;
  totalClients: number;
  totalDevices: number;
  activeDevices: number;
  warnings: string[];
  clients: ClientRow[];
  devices: DeviceRow[];
};

type DeviceFilter = 'all' | 'active' | 'inactive';

function LastSeen({ value, lookbackDays }: { value: number | null; lookbackDays: number }): ReactElement {
  if (!value) {
    return <span className="text-gray-500">None in {lookbackDays} days</span>;
  }
  return <span title={moment(value).format('LLL')}>{moment(value).fromNow()}</span>;
}

function Calibration({ value }: { value: string | null }): ReactElement {
  if (!value) return <span className="text-gray-500">Never</span>;
  return <span title={value}>{moment(value).format('MMM D, YYYY')}</span>;
}

function Payment({ value }: { value: LastPayment | null }): ReactElement {
  if (!value) return <span className="text-gray-500">None</span>;
  const amount =
    value.amount !== null && value.amount !== undefined
      ? `${value.currency ?? ''} ${value.amount.toFixed(2)}`.trim()
      : null;
  return (
    <span title={moment(value.date).format('LLL')}>
      {moment(value.date).format('MMM D, YYYY')}
      <span className="block text-xs text-gray-500">
        {[value.planName, amount].filter(Boolean).join(' · ') || '—'}
      </span>
    </span>
  );
}

function Tile({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: boolean }): ReactElement {
  return (
    <div
      className={`rounded border bg-container-500 px-4 py-3 text-sm ${
        accent ? 'border-success-500/40' : 'border-gray-800'
      }`}
    >
      <div className={accent ? 'text-success-400' : 'text-gray-400'}>{label}</div>
      <div className={`text-xl font-semibold ${accent ? 'text-success-400' : ''}`}>
        {value}
        {sub && <span className="ml-2 text-xs font-normal text-gray-400">{sub}</span>}
      </div>
    </div>
  );
}

/**
 * Cross-client overview for TelosAir staff: every client and device, which
 * sensors are reporting, last calibration and last payment.
 * @returns The admin overview page.
 */
export default function AdminOverviewPage(): ReactElement {
  const userEmail = useMemo(() => getUserNameFromSessionStorage(), []);
  const isAllowed = userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const [data, setData] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState('');
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilter>('all');
  const [clientFilter, setClientFilter] = useState<number | null>(null);

  useEffect(() => {
    if (!isAllowed) return undefined;
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get<AdminOverview>(`${DEV_NEW_DOMAIN_URL}/admin/overview`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load overview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAllowed, reloadKey]);

  const devices = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.devices.filter((d) => {
      if (clientFilter !== null && d.clientId !== clientFilter) return false;
      if (deviceFilter === 'active' && !d.active) return false;
      if (deviceFilter === 'inactive' && d.active) return false;
      if (!q) return true;
      return (
        String(d.serialNumber).includes(q) ||
        (d.deviceName ?? '').toLowerCase().includes(q) ||
        d.clientName.toLowerCase().includes(q)
      );
    });
  }, [data, search, deviceFilter, clientFilter]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  const activeShare =
    data && data.totalDevices > 0 ? Math.round((data.activeDevices / data.totalDevices) * 100) : 0;
  const selectedClient = data?.clients.find((c) => c.clientId === clientFilter) ?? null;

  return (
    <div className="flex flex-1 flex-col gap-6 px-6 py-6 text-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Overview (Admin)</h1>
          <p className="text-sm text-gray-400">
            {data
              ? `Updated ${moment(data.generatedAt).format('LT')} · active = a reading in the last ${data.activeWindowMinutes} min`
              : `Signed in as ${userEmail}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Tile label="Clients" value={data?.totalClients ?? '—'} />
          <Tile label="Devices" value={data?.totalDevices ?? '—'} />
          <Tile
            label="Active sensors"
            value={data?.activeDevices ?? '—'}
            sub={data && data.totalDevices > 0 ? `${activeShare}%` : undefined}
            accent
          />
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
            className="self-center rounded border border-gray-800 bg-container-500 px-3 py-2 text-sm text-gray-200 transition hover:bg-background-500 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded border border-error-500 bg-error-500/10 px-4 py-3 text-sm text-error-400">
          {error}
        </div>
      )}
      {data && data.warnings.length > 0 && (
        <div className="rounded border border-warning-500/50 bg-warning-500/10 px-4 py-3 text-sm text-warning-300">
          Some data could not be loaded:
          <ul className="ml-4 list-disc">
            {data.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {loading && !data ? (
        <div className="flex min-h-[16rem] items-center justify-center">
          <Loader />
        </div>
      ) : data ? (
        <>
          <section className="rounded border border-gray-800 bg-container-500">
            <h2 className="border-b border-gray-800 px-4 py-3 text-lg font-semibold">Clients</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-gray-400">
                  <tr className="border-b border-gray-800">
                    <th className="px-4 py-2">Client</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Devices</th>
                    <th className="px-4 py-2 text-right">Active</th>
                    <th className="px-4 py-2">Last reading</th>
                    <th className="px-4 py-2">Last calibrated</th>
                    <th className="px-4 py-2">Last payment</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clients.map((c) => (
                    <tr
                      key={c.clientId}
                      onClick={() => setClientFilter(clientFilter === c.clientId ? null : c.clientId)}
                      className={`cursor-pointer border-b border-gray-800/60 hover:bg-background-500 ${
                        clientFilter === c.clientId ? 'bg-background-500' : ''
                      }`}
                    >
                      <td className="px-4 py-2">
                        {c.clientName}
                        <span className="ml-2 text-xs text-gray-500">#{c.clientId}</span>
                      </td>
                      <td className="px-4 py-2 text-gray-400">{c.status ?? '—'}</td>
                      <td className="px-4 py-2 text-right">{c.deviceCount}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={c.activeDevices > 0 ? 'text-success-400' : 'text-gray-500'}>
                          {c.activeDevices}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <LastSeen value={c.lastSeen} lookbackDays={data.lastSeenLookbackDays} />
                      </td>
                      <td className="px-4 py-2">
                        <Calibration value={c.lastCalibration} />
                      </td>
                      <td className="px-4 py-2">
                        <Payment value={c.lastPayment} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded border border-gray-800 bg-container-500">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-800 px-4 py-3">
              <h2 className="text-lg font-semibold">
                Devices
                <span className="ml-2 text-sm font-normal text-gray-400">
                  {devices.length}
                  {selectedClient ? ` · ${selectedClient.clientName}` : ''}
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                {selectedClient && (
                  <button
                    type="button"
                    onClick={() => setClientFilter(null)}
                    className="rounded border border-gray-700 px-2 py-1 text-xs text-gray-300 hover:bg-background-500"
                  >
                    {selectedClient.clientName} ✕
                  </button>
                )}
                {(['all', 'active', 'inactive'] as DeviceFilter[]).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setDeviceFilter(f)}
                    className={`rounded border px-2 py-1 text-xs ${
                      deviceFilter === f
                        ? 'border-primary-500 text-primary-300'
                        : 'border-gray-700 text-gray-400 hover:bg-background-500'
                    }`}
                  >
                    {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Not reporting'}
                  </button>
                ))}
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Serial, name or client…"
                  className="w-56 rounded border border-gray-800 bg-container-700 px-3 py-1.5 text-sm text-gray-100 placeholder-gray-500 focus:border-primary-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="max-h-[36rem] overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-container-500 text-xs text-gray-400">
                  <tr className="border-b border-gray-800">
                    <th className="px-4 py-2">Serial</th>
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Client</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Sensor</th>
                    <th className="px-4 py-2">Last reading</th>
                    <th className="px-4 py-2">Last calibrated</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                        No devices match.
                      </td>
                    </tr>
                  )}
                  {devices.map((d) => (
                    <tr key={`${d.clientId}-${d.serialNumber}`} className="border-b border-gray-800/60">
                      <td className="px-4 py-2 font-mono">{d.serialNumber}</td>
                      <td className="px-4 py-2">{d.deviceName || '—'}</td>
                      <td className="px-4 py-2 text-gray-400">{d.clientName}</td>
                      <td className="px-4 py-2 text-gray-400">
                        {d.deviceType ? d.deviceType.charAt(0) + d.deviceType.slice(1).toLowerCase() : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`rounded border px-2 py-0.5 text-xs ${
                            d.active
                              ? 'border-success-500/50 bg-success-500/15 text-success-400'
                              : 'border-gray-600 bg-gray-500/10 text-gray-400'
                          }`}
                        >
                          {d.active ? 'Active' : 'Not reporting'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <LastSeen value={d.lastSeen} lookbackDays={data.lastSeenLookbackDays} />
                      </td>
                      <td className="px-4 py-2">
                        <Calibration value={d.lastCalibration} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
